"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote, CircleCheck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Sarah Jenkins",
  role: "North York Resident",
  company: "RMT Client",
  quote: "The therapeutic massage at Yonge Sheppard was transformative. My chronic neck pain has significantly improved, and the clinical expertise of the staff is truly unmatched in the area.",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653134/img/testimonial_sarah_north_york.png"
}, {
  id: 2,
  name: "David Chen",
  role: "Wellness Professional",
  company: "Acupuncture Client",
  quote: "The acupuncture and facial treatments here are exceptional. I've noticed a significant improvement in my skin clarity and overall stress levels since starting my sessions at this clinic.",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653134/img/testimonial_david_sheppard.png"
}, {
  id: 3,
  name: "Elena Rodriguez",
  role: "Wellness Advocate",
  company: "Regular Patient",
  quote: "I appreciate the direct billing and the professional atmosphere. They make the insurance process seamless so I can focus entirely on my recovery and wellness journey.",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1653134/img/testimonial_elena_wellness.png"
}];
export default function CustomerExperiences() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="239764" id="voices-of-recovery" className="relative w-full py-24 md:py-32 bg-accent/30 text-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,var(--color-chart-2)_0%,transparent_70%)] opacity-10 pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <Badge variant="outline" className="px-3 py-1 text-sm border-primary/20 bg-background/50 text-primary font-default">
            Voices of Recovery
          </Badge>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight text-primary max-w-2xl">
            Client Experiences at Yonge Sheppard
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl font-default">
            Discover how our clinical sanctuary in North York helps our community find balance and healing.
          </p>
        </div>

        <div className="max-w-5xl mx-auto relative">
          <Carousel opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                  <div className="p-2 md:p-4">
                    <Card className="relative overflow-hidden border-border/30 bg-background/60 backdrop-blur-md shadow-xl rounded-3xl transition-all duration-300 hover:shadow-2xl hover:border-border/50">
                      {}
                      <Quote className="absolute -top-6 -right-6 size-48 text-chart-2/10 -rotate-12 pointer-events-none" />
                      
                      <CardContent className="p-8 md:p-12 flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-start relative z-10">
                        
                        {}
                        <div className="flex flex-col items-center md:items-start gap-5 shrink-0 w-full md:w-64">
                          <SafeHydrate><Avatar className="size-28 border-2 border-background shadow-md">
                            <AvatarImage src={testimonial.avatarUrl} alt={testimonial.name} className="object-cover" />
                            <AvatarFallback className="text-2xl font-default font-medium bg-accent text-accent-foreground">
                              {testimonial.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          
                          <div className="text-center md:text-left space-y-1.5">
                            <div className="flex items-center justify-center md:justify-start gap-2">
                              <h3 className="font-default font-semibold text-xl text-foreground">
                                {testimonial.name}
                              </h3>
                              <Badge variant="secondary" className="bg-chart-2/10 text-chart-2 hover:bg-chart-2/20 border-transparent px-1.5 py-0 h-5">
                                <CircleCheck className="size-3 mr-1" />
                                <span className="text-[10px] uppercase tracking-wider font-bold">Verified</span>
                              </Badge>
                            </div>
                            <p className="text-sm font-medium text-muted-foreground font-default">
                              {testimonial.role}
                            </p>
                            <p className="text-sm text-muted-foreground/80 font-default">
                              {testimonial.company}
                            </p>
                          </div>
                        </div>

                        {}
                        <div className="flex-1 flex items-center min-h-[160px]">
                          <p className="text-xl md:text-2xl lg:text-3xl italic font-serif text-foreground/90 leading-relaxed text-center md:text-left">
                            "{testimonial.quote}"
                          </p>
                        </div>

                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="hidden md:block">
              <CarouselPrevious className="absolute -left-4 lg:-left-12 top-1/2 -translate-y-1/2 size-12 bg-background/80 backdrop-blur-sm border-border/40 hover:bg-background hover:scale-105 transition-all shadow-md text-primary" />
              <CarouselNext className="absolute -right-4 lg:-right-12 top-1/2 -translate-y-1/2 size-12 bg-background/80 backdrop-blur-sm border-border/40 hover:bg-background hover:scale-105 transition-all shadow-md text-primary" />
            </div>
            
            {}
            <div className="flex justify-center gap-4 mt-6 md:hidden">
              <CarouselPrevious className="static translate-y-0 size-12 bg-background/80 backdrop-blur-sm border-border/40 hover:bg-background shadow-sm text-primary" />
              <CarouselNext className="static translate-y-0 size-12 bg-background/80 backdrop-blur-sm border-border/40 hover:bg-background shadow-sm text-primary" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}